import { Grid, GridProps, Typography } from "@mui/material";
import { LABEL_WIDTH } from "../constants";

/** Responsive width props for one labeled filter in a `Grid container` row. */
export type BulkSalaryIncreaseFilterCellWidths = Partial<
  Pick<GridProps, "xs" | "sm" | "md" | "lg">
>;

/** Reusable `itemWidths` presets for new bulk-salary filter rows. */
export const bulkSalaryIncreaseFilterGridWidths: {
  threePerRowFromMd: BulkSalaryIncreaseFilterCellWidths;
  fixedThird: BulkSalaryIncreaseFilterCellWidths;
} = {
  /** Three filters per row from `md` (full-width stack on narrow screens). */
  threePerRowFromMd: { xs: 12, md: 4 },
  /** Table-style row: three equal columns (`xs={4}`) at every breakpoint. */
  fixedThird: { xs: 4 },
};

/** Occupies one `xs={4}` column so a two-field row still lines up with three-column rows. */
export const BulkSalaryIncreaseFilterRowEmptySlot = () => (
  <Grid item xs={4} aria-hidden role="presentation" />
);

type Props = {
  label: React.ReactNode;
  children: React.ReactNode;
  /** Default: three filters per row from `md` up (`xs: 12` stacks on narrow screens). */
  itemWidths?: BulkSalaryIncreaseFilterCellWidths;
};

const defaultThreeColumnRow: BulkSalaryIncreaseFilterCellWidths =
  bulkSalaryIncreaseFilterGridWidths.threePerRowFromMd;

/**
 * One label + control column for bulk salary filter grids.
 * Use inside a parent `<Grid container>`; pass `itemWidths` to tune how many sit on one row.
 */
const BulkSalaryIncreaseFilterCell = ({
  label,
  children,
  itemWidths = defaultThreeColumnRow,
}: Props) => (
  <Grid item {...itemWidths}>
    <Grid container alignItems="center" columnSpacing={2} wrap="nowrap">
      <Grid
        item
        sx={{
          width: LABEL_WIDTH,
          textAlign: "end",
          flexShrink: 0,
        }}
      >
        <Typography variant="secondary" component="span">
          {label}
        </Typography>
      </Grid>
      <Grid item xs sx={{ minWidth: 0 }}>
        {children}
      </Grid>
    </Grid>
  </Grid>
);

export default BulkSalaryIncreaseFilterCell;
